import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ToastProvider } from '@/components/ui/Toast';
import { OfflineIndicator } from '@/components/layout/OfflineIndicator';
import { Login } from '@/pages/Login';
import { Home } from '@/pages/Home';
import { Audit } from '@/pages/Audit';
import { Dashboard } from '@/pages/Dashboard';
import { StoreDetail } from '@/pages/StoreDetail';

// Componente de rota protegida
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen bg-page-bg flex items-center justify-center">
                <div className="text-center">
                    <div className="spinner mx-auto mb-4" />
                    <p className="text-content-muted animate-pulse">Carregando...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
}

// Componente de rota pública (redireciona se já logado)
function PublicRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen bg-page-bg flex items-center justify-center">
                <div className="spinner" />
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
}

export function App() {
    return (
        <ToastProvider>
            <BrowserRouter>
                {/* Indicador de offline - global */}
                <OfflineIndicator />

                <Routes>
                    {/* Rotas públicas */}
                    <Route
                        path="/login"
                        element={
                            <PublicRoute>
                                <Login />
                            </PublicRoute>
                        }
                    />

                    {/* Rotas protegidas */}
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <Home />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/audit/:storeId"
                        element={
                            <ProtectedRoute>
                                <Audit />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/store/:storeId"
                        element={
                            <ProtectedRoute>
                                <StoreDetail />
                            </ProtectedRoute>
                        }
                    />

                    {/* Fallback - redireciona para home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </ToastProvider>
    );
}

export default App;
